import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { ErrorOutlineOutlined } from "@mui/icons-material";
import MDButton from "components/MDButton";
import { useDispatch, useSelector } from "react-redux";
import { retryTransfer } from "store/slice/transactions/transactionSlice";

export default function RetryTransferDialog({
  open,
  setNotification,
  onClose,
  transferData = {},
}) {
  const { retrying, error, success } = useSelector((s) => s.transactions);
  const latestTx =
    useSelector((s) =>
      s.transactions.list?.find((t) => t._id === transferData?._id)
    ) || transferData;

  const extra = latestTx?.paymentInfo?.extraParams || {};
  const place = latestTx?.placeId || {};
  const primary = {
    ...(extra.primaryTransfer || {}),
    destination: place?.connectAccountId,
  };
  const secondary = {
    ...(extra.secondaryTransfer || {}),
    destination: place?.splitRevenue?.secondaryConnectAccountId,
  };

  const dispatch = useDispatch();

  useEffect(() => {
    if (!open) return;
    const bothSucceeded =
      primary?.success === true && secondary?.success === true;
    if (bothSucceeded) {
      setNotification({
        color: "success",
        icon: "check",
        title: "Transfer Successful",
        content:
          "Both primary and secondary transfers were completed successfully.",
        show: true,
        close: () => setNotification((prev) => ({ ...prev, show: false })),
      });

      onClose();
    }
  }, [open, primary?.success, secondary?.success, onClose]);

  const handleConfirm = () => {
    dispatch(
      retryTransfer({
        ...transferData,
        paymentInfo: {
          ...transferData.paymentInfo,
          extraParams: {
            ...transferData.paymentInfo.extraParams,
            primaryTransfer: primary,
            secondaryTransfer: secondary,
          },
        },
      })
    );
  };

  const sections = [
    { key: "primary", label: "Primary Transfer", data: primary },
    { key: "secondary", label: "Secondary Transfer", data: secondary },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle textAlign="center">Retry Transfer</DialogTitle>

      <DialogContent dividers>
        {error?.message && (
          <Typography
            color="error"
            align="center"
            sx={{ mb: 2, fontSize: "0.875rem" }}
          >
            <ErrorOutlineOutlined sx={{ mr: 1 }} />
            {error.message}
          </Typography>
        )}

        {sections.map(({ key, label, data }) => {
          const isFailed = data.success === false;
          const StatusIcon = isFailed
            ? CancelOutlinedIcon
            : CheckCircleOutlineIcon;
          const iconColor = isFailed ? "error.main" : "success.main";

          return (
            <Card
              key={key}
              variant="outlined"
              sx={{ bgcolor: (theme) => theme.palette.grey[50], mb: 1 }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Typography variant="h6">{label}</Typography>
                  <StatusIcon sx={{ color: iconColor }} />
                </Stack>

                {isFailed && data.errorMessage && (
                  <Typography
                    color="secondary"
                    sx={{ mb: 1, fontSize: "0.775rem" }}
                  >
                    Reason: {data.errorMessage}
                  </Typography>
                )}

                <Stack>
                  {[
                    {
                      label: "Amount",
                      value:
                        data.amount != null
                          ? `$${(data.amount / 100).toFixed(2)}`
                          : "—",
                    },
                    {
                      label: "Destination",
                      value: data.destination || "—",
                    },
                  ].map(({ label, value }) => (
                    <Grid
                      key={label}
                      container
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ px: 1, py: 0.5 }}
                    >
                      <Grid item>
                        <Typography variant="body2" color="textSecondary">
                          {label}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant="caption" fontSize={14}>
                          {value}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </DialogContent>

      <DialogActions>
        <MDButton onClick={onClose}>Cancel</MDButton>
        <MDButton
          variant="gradient"
          color="info"
          disabled={retrying}
          onClick={handleConfirm}
        >
          {retrying ? "Processing..." : "Confirm Retry"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
